import PropTypes from 'prop-types'
import moment from 'moment-wrapper'
import styled from 'styled-components'
import { userTimeZoneFormat } from 'utils/time'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import * as sessionUserSelector from 'selectors/sessionUser'

import { capitalizeFirstLetter } from 'utils/strings'
import colors from 'components/utils/colors'

import Tooltip from 'components/Shared/Tooltip/Index'
import Badge from 'components/Shared/Badge/Index'
import MarkedText from 'components/Shared/MarkedText/Index'
import RichTextEditor from 'components/Shared/RichTextEditor/Index'

const StyledWrapper = styled.div`
  padding: 6px 4px 10px 4px;
`

const CommentMetaLine = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 4px;
  gap: 4px;
`

const CommentAuthor = styled.div`
  color: ${colors.gray40};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
`

const CommentMeta = styled.div`
  color: ${colors.gray40};
  font-size: 12px;
  line-height: 16px;
  width: fit-content;
`

const CommentText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.secondary20};
  line-height: initial;
  white-space: pre-line;
  word-break: break-word;
`

const CommentRichText = styled.div`
  font-size: 14px;
  color: ${colors.secondary20};
  line-height: initial;
  white-space: pre-line;
  word-break: break-word;
`

const metaInfo = (comment, t, userTimeZone) => {
  const date = moment(comment.created)
  const created = userTimeZoneFormat(userTimeZone, comment.created)

  return {
    commentMeta: t('ticketPage.commentedAt', { date: created }),
    commentTooltip: date.fromNow(false),
  }
}

const CommentCellContent = ({ comment, searchValue, hasRichText }) => {
  const { t } = useTranslation()
  const userTimeZone = useSelector(sessionUserSelector.timezone)
  const tooltipId = comment.id
  const metaParams = metaInfo(comment, t, userTimeZone)

  return (
    <StyledWrapper>
      <CommentMetaLine data-test='comment-meta'>
        <Badge color={colors.secondary80} margin='0'>
          {capitalizeFirstLetter(comment.owner)}
        </Badge>
        <CommentAuthor data-test='comment-author'>{comment.author}</CommentAuthor>
        <CommentMeta
          data-test='comment-date'
          data-tooltip-content={metaParams.commentTooltip}
          data-tooltip-id={tooltipId}
        >
          {metaParams.commentMeta}
        </CommentMeta>
      </CommentMetaLine>
      <Tooltip id={tooltipId} />
      {hasRichText
        ? (
          <CommentRichText data-test='comment-text'>
            <RichTextEditor isReadOnly value={comment.body} />
          </CommentRichText>
          )
        : (
          <CommentText data-test='comment-text'>
            <MarkedText text={comment.body} markedPart={searchValue} />
          </CommentText>
          )
      }
    </StyledWrapper>
  )
}

CommentCellContent.propTypes = {
  comment: PropTypes.object.isRequired,
  searchValue: PropTypes.string,
  hasRichText: PropTypes.bool,
}

export default CommentCellContent
